import React, { useState, useMemo, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import axios from 'axios'
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Button, Card, CardContent, CardHeader, Typography,
  Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import { Spin } from 'antd';

import {
  getAllSubscriptionPackages,
  getUserCurrentSubscription,
  getUserBillingHistory,
  cancelSubscription,
  updateBillingCardInfo,
  undoSubscriptionCancel
} from '../../slices/auth.js';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import AgGridReact from "../../components/ag-grid-table/index.js";
import Pagination from '../../components/pagination';
import { convertUtcToEst } from '../../utils/helpers.js';
import { BillingWrapper } from './style';
import { BsEye } from 'react-icons/bs';
import { Download } from '@mui/icons-material';

const Billing = () => {
  const dispatch = useDispatch()
  const {
    fetchingCurrentActiveSubscription,
    currentActiveSubscription,
    fetchingAllSubscriptionPackages,
    updatingUndoSubscriptionCancel,
    cancellingSubscription,
    fetchingUserBillingHistory,
    updatingBillCardInfo
   } = useSelector((state) => state.auth)

  const [subscriptionList, setSubscriptionList] = useState([])
  const [billingHistory, setBillingHistory] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const currentPlan = useMemo(() => {
    if (!currentActiveSubscription) return null;
    const { canceled_at, next_billing_at } = currentActiveSubscription

    const nextBillingDate = currentActiveSubscription.canceled_at
      ? "---"
      : convertUtcToEst(next_billing_at, { year: 'numeric', month: 'long', day: '2-digit' });

    const billingMessage = currentActiveSubscription.canceled_at
      ? 'Subscription Canceled'
      : `Next billing: ${convertUtcToEst(next_billing_at, { year: 'numeric', month: 'long', day: '2-digit' })}`;

    return {
      name: currentActiveSubscription.package_name,
      price: currentActiveSubscription.price,
      billing: currentActiveSubscription.interval,
      nextBilling: nextBillingDate || "---",
      status: currentActiveSubscription.status === 'active' ? 'active' : 'inactive',
      subscriptionCanceled: canceled_at ? true : false,
      cardInfo: currentActiveSubscription?.card,
      subscriptionId: currentActiveSubscription?.stripe_subscription_id,
      billingMessage: billingMessage
    };
  }, [currentActiveSubscription]);


  const plans = useMemo(() => {
    return subscriptionList?.map(plan => {
      let icon = InventoryIcon;
      if (plan?.name === 'Growth') icon = TrendingUpIcon;
      if (plan?.name === 'Enterprise') icon = StarIcon;

      let features = [];
      try {
        const parsed = typeof plan?.description === 'string'
          ? JSON.parse(plan.description)
          : plan.description;

        features = parsed?.feature || parsed?.features || [];
      } catch {
        features = [];
      }
      return {
        id: plan?.id,
        name: plan?.name,
        price: plan?.price,
        billing: plan?.interval,
        features: features,
        icon,
        current: plan?.name === currentPlan?.name,
      };
    });
  }, [subscriptionList, currentPlan]);


  useEffect(() => {
    dispatch(getAllSubscriptionPackages()).then(res => {
      setSubscriptionList(res?.payload?.data)
    })
    dispatch(getUserCurrentSubscription())
  }, [])

  const fetchBillingHistory = () => {
    dispatch(getUserBillingHistory({ page: pageNumber, limit: pageSize }))
      .then(res => {
        if (res?.payload) {
          setBillingHistory(res?.payload);
          setTotalCount(res.payload?.pagination?.total);
        }
      });
  };

  useEffect(() => {
    fetchBillingHistory();
  }, [pageNumber, pageSize]);

  const handlePagination = (value, type) => {
    if (type === 'pageSize') {
      setPageSize(parseInt(value));
      setPageNumber(1);
    } else if (type === 'pageNumber') {
      setPageNumber(parseInt(value));
    }
  };

  const refreshGridClick = () => {
    fetchBillingHistory();
  };

  const desiredOrder = ['Starter', 'Standard', 'Enterprise'];

  const orderedPlans = plans?.sort(
    (a, b) => desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name)
  );

  const debouncedSubscribe = useCallback(
    debounce(async (planId) => {
      try {
        const res = await axios.post(`${process.env.OPULENT_NODE_API_URL}/payment/subscribe`, {
          planId,
        });

        const checkoutUrl = res?.data?.checkoutUrl;
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('Error creating checkout session:', error);
      }
    }, 500, { leading: true, trailing: false }),
    []
  );

  const debouncedUndo = useCallback(
    debounce((subscriptionId) => {
      handleUndoCancel(subscriptionId);
    }, 1000, { leading: true, trailing: false }),
    []
  );

  const handleUndoCancel = async (subscriptionId) => {
    dispatch(undoSubscriptionCancel({ subscriptionId })).then(() => {
      dispatch(getUserCurrentSubscription())
    })
  }


  const debouncedUpdateCardRedirect = useCallback(
    debounce(async () => {
      try {
        const res = await dispatch(updateBillingCardInfo());
        if (res.payload?.url) {
          window.open(res.payload.url, '_blank');
        }
      } catch (err) {
        console.error('Error opening Stripe portal:', err);
        alert('Could not open Stripe portal');
      }
    }, 300),
    []
  );

  // AG Grid column definitions
  const columnDefs = [
    {
      headerName: 'Date',
      field: 'created_at',
      flex: 1,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      valueFormatter: ({ value }) => convertUtcToEst(value, { year: 'numeric', month: '2-digit', day: '2-digit' })
    },
    {
      headerName: 'Amount',
      field: 'amount',
      flex: 1,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      valueFormatter: ({ value }) => `$${value?.toFixed(2)}`
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRendererFramework: ({ value }) => {
        const status = value || 'Active';
        const getStatusColor = (status) => {
          switch (status.toLowerCase()) {
            case 'paid':
              return 'success';
            case 'unpaid':
              return 'error';
            default:
              return 'primary';
          }
        };

        return (
          <Chip
            label={status}
            color={getStatusColor(status)}
            size="small"
            variant="outlined"
            className='billing-card-header-chip'
          />
        );
      }
    },
    {
      headerName: 'Invoice',
      field: 'invoice_id',
      flex: 1,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRendererFramework: (params) => {
        const totalRows = params.api.getDisplayedRowCount();
        console.log('Total Rows:', totalRows);
        const descendingIndex = totalRows - params.node.rowIndex;
        return (
          <span className='billing-table-id'>
            INV-{descendingIndex.toString().padStart(3, '0')}
          </span>
        );
      }
    },
    {
      headerName: '',
      field: 'invoice_url',
      flex: 1,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRendererFramework: ({ value }) => {
        return value ? <a href={value} target="_blank" rel="noopener noreferrer" className='text-black'>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download w-4 h-4" data-lov-id="src/components/account/BillingSubscription.tsx:209:22" data-lov-name="Download" data-component-path="src/components/account/BillingSubscription.tsx" data-component-line="209" data-component-file="BillingSubscription.tsx" data-component-name="Download" data-component-content="%7B%22className%22%3A%22w-4%20h-4%22%7D"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
        </a> : '---'
      }

    }
  ];

  return (
    <BillingWrapper>
      <Spin spinning={fetchingAllSubscriptionPackages || fetchingCurrentActiveSubscription}>
        <Box maxWidth="1150px" mx="auto" py={{ xs: 2, sm: 3, md: 4 }} px={{ xs: 1, sm: 2, md: 2 }}>
          <div className='billing-heading-container'>
            <Typography variant="h1" className='billing-heading' gutterBottom>
              Billing & Subscription
            </Typography>
            <Typography variant="subtitle1" className='billing-sub-heading' gutterBottom>
              Manage your subscription, payment methods, and billing history
            </Typography>
          </div>

          {/* Current Plan */}
          {
            currentPlan && currentPlan.status === 'active' && (
              <>
                <Card variant="outlined" className='billing-card' sx={{ mt: 3 }}>
                  <CardHeader
                    avatar={
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-credit-card w-5 h-5 text-blue-600" data-lov-id="src/components/account/BillingSubscription.tsx:65:12" data-lov-name="CreditCard" data-component-path="src/components/account/BillingSubscription.tsx" data-component-line="65" data-component-file="BillingSubscription.tsx" data-component-name="CreditCard" data-component-content="%7B%22className%22%3A%22w-5%20h-5%20text-blue-600%22%7D"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                    }
                    title="Current Plan"
                    titleTypographyProps={{ variant: 'h6' }}
                    className='billing-card-header'
                  />
                  <CardContent className='billing-card-content'>
                    <Box
                      display="flex"
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      borderColor="#dbeafe"
                      bgcolor="#eff6ff"
                      p={{ xs: 1.5, sm: 2 }}
                      borderRadius={2}
                      gap={{ xs: 2, sm: 0 }}
                    >
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                          <Typography variant="h6" color="#1e41af" className='billing-card-header-title'>{currentPlan?.name}</Typography>
                          <Chip className='billing-card-header-chip' label={currentPlan?.status} color="primary" size="small" />
                        </Box>
                        <Typography color="#1e41af" fontSize={{ xs: '0.875rem', sm: '1rem' }}>
                          ${currentPlan?.price}/{currentPlan?.billing} • {currentPlan?.billingMessage}
                        </Typography>
                      </Box>
                      <Box textAlign={{ xs: 'left', sm: 'right' }} width={{ xs: '100%', sm: 'auto' }}>
                        {!currentPlan?.subscriptionCanceled &&
                          <Button variant="outlined" className='billing-card-button' size="small" color="error" onClick={() => setShowCancelDialog(true)} fullWidth={{ xs: true, sm: false }}>
                            {cancellingSubscription || fetchingCurrentActiveSubscription ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              'Cancel Plan'
                            )}
                          </Button>
                        }
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                {/* Payment Method */}
                {
                  !currentPlan.subscriptionCanceled && (
                    <Card variant="outlined" className='billing-card' sx={{ mt: 3 }}>
                      <CardHeader className='billing-card-header' title="Payment Method" titleTypographyProps={{ variant: 'h6' }} />
                      <CardContent className='billing-card-content'>
                        <Box
                          display="flex"
                          flexDirection={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          bgcolor="#f9f9f9"
                          p={{ xs: 1.5, sm: 2 }}
                          borderRadius={2}
                          gap={{ xs: 2, sm: 0 }}
                        >
                          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <Box display="flex" alignItems="center" justifyContent="center" bgcolor=" #dbeafe" width={40} height={40} borderRadius={2} flexShrink={0}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-credit-card w-5 h-5 text-blue-600" data-lov-id="src/components/account/BillingSubscription.tsx:65:12" data-lov-name="CreditCard" data-component-path="src/components/account/BillingSubscription.tsx" data-component-line="65" data-component-file="BillingSubscription.tsx" data-component-name="CreditCard" data-component-content="%7B%22className%22%3A%22w-5%20h-5%20text-blue-600%22%7D"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                            </Box>
                            <Box>
                              <Typography color="#000" fontWeight="500" fontSize={{ xs: '0.875rem', sm: '1rem' }}>{currentPlan?.cardInfo?.brand.toUpperCase()} ending in {currentPlan?.cardInfo?.last4}</Typography>
                              <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>Expires {currentPlan?.cardInfo?.exp_month}/{currentPlan?.cardInfo?.exp_year}</Typography>
                            </Box>
                          </Box>
                          <Box width={{ xs: '100%', sm: 'auto' }}>
                            <Button className='billing-card-button text-black' variant="outlined" size="small" disabled={updatingBillCardInfo} onClick={debouncedUpdateCardRedirect} fullWidth={{ xs: true, sm: false }}>Update Card</Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )
                }
              </>
            )
          }



          {/* Available Plans */}
          <Card variant="outlined" className='billing-card' sx={{ mt: 3 }}>
            <CardHeader className='billing-card-header' title="Available Plans" titleTypographyProps={{ variant: 'h6' }} />
            <CardContent className='billing-card-content'>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {orderedPlans?.map((plan) => {
                  const Icon = plan.icon;
                  const isCurrent = plan.current;
                  const isGrowth = plan.name === 'Standard'
                  const showSubscribeButton = !isCurrent && isGrowth;

                  return (
                    <Grid item xs={12} sm={6} lg={4} key={plan.name}>
                      <Box
                        p={{ xs: 2, sm: 3 }}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                        height="100%"
                        minHeight={{ xs: "200px", sm: "240px" }}
                        border={2}
                        borderColor={isCurrent ? 'primary.light' : 'grey.300'}
                        bgcolor={isCurrent ? '#eff6ff' : 'grey.100'}
                        borderRadius={2}
                        position="relative"
                        sx={{
                          opacity: isGrowth ? 1 : 0.5,
                          pointerEvents: isGrowth ? 'auto' : 'none',
                        }}
                      >
                        {/* Coming Soon Overlay for non-Growth plans */}
                        {!isGrowth && (
                          <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="#f3f4f6cc"
                            borderRadius={2}
                            zIndex={1}
                          >
                            <Chip
                              label="Coming Soon"
                              size="small"
                              className='billing-card-header-chip'
                              sx={{
                                color: "#4b5563 !important",
                                backgroundColor: "#d9d9d9 !important",
                                borderColor: "#e4e6e9 !important",
                              }}
                            />
                          </Box>
                        )}
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Icon fontSize="small" color={plan?.name === 'Growth' ? 'primary' : 'disabled'} />
                            <Typography color="#000" fontWeight="600">{plan?.name}</Typography>
                            {isCurrent && (
                              <Chip className='billing-card-header-chip' label="Current" size="small" color="primary" />
                            )}
                          </Box>

                          <Typography variant="h5" fontWeight={700} color="#000" className='mb-2' fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                            ${plan?.price}{' '}
                            <Typography variant="caption" className='grey-text' component="span" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>/{plan?.billing}</Typography>
                          </Typography>

                          <Box mt={1} mb={2}>
                            {plan?.features?.map((feature, index) => (
                              <Typography
                                key={index}
                                variant="body2"
                                color="text.secondary"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                className='mb-2'
                              >
                                • {feature}
                              </Typography>
                            ))}
                          </Box>
                        </Box>

                        {showSubscribeButton && (
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => debouncedSubscribe(plan.id)}
                          >
                            Subscribe
                          </Button>
                        )}
                        {isGrowth && currentPlan?.subscriptionCanceled && (
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => debouncedUndo(currentPlan.subscriptionId)}
                          >
                            {updatingUndoSubscriptionCancel ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              'Re-subscribe'
                            )}
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>


          <Card className='billing-card' variant="outlined" sx={{ mt: 3, height: { xs: 400, sm: 480 } }}>
            <CardHeader className='billing-card-header' title="Billing History" titleTypographyProps={{ variant: 'h6' }} />
            <CardContent className='billing-card-content pb-0'>
              <div className="ag-theme-alpine" style={{ height: '100%', width: '100%', minHeight: '200px' }}>
                <Spin spinning={fetchingUserBillingHistory}>
                  <AgGridReact
                    user_referrals={billingHistory?.data}
                    rowHeight={68}
                    headerHeight={48}
                    height={"755px"}
                    columnDefinitions={columnDefs}
                    rowSelection='multiple'
                    className="billing-records-table"
                    getRowNodeId={(params) => params?.data?.invoice_id || params?.data?.subscribed_at || Math.random().toString()}
                    suppressMenuHide={true}
                    suppressMenu={true}
                    suppressColumnMoveAnimation={true}
                    suppressColumnVirtualisation={false}
                    suppressRowVirtualisation={false}
                    suppressHeaderMenuButton={true}
                    suppressColumnMenu={true}
                    suppressContextMenu={true}
                    suppressRowClickSelection={true}
                    suppressCellFocus={true}
                    allowContextMenuWithControlKey={false}
                    suppressRowContextMenu={true}
                    suppressCellContextMenu={true}
                    onCellContextMenu={(params) => params.event.preventDefault()}
                  />
                  {/* <Pagination
                    total={totalCount}
                    totalPages={Math.ceil(totalCount / pageSize + 1)}
                    pageNumber={pageNumber}
                    pageLimit={pageSize}
                    handleLimitChange={(e) => handlePagination(e, 'pageSize')}
                    handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
                    handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
                    handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
                    refreshGridClick={refreshGridClick}
                    className='billing-pagination'
                  /> */}
                </Spin>
              </div>
            </CardContent>
          </Card>
        </Box>
        <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
          <DialogTitle>Cancel Subscription</DialogTitle>
          <DialogContent>
            <Typography>
              This will cancel your subscription, but you'll still have access until the end of your billing cycle.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCancelDialog(false)}>Close</Button>
            <Button
              onClick={() => {
                dispatch(cancelSubscription()).then(() => {
                  dispatch(getUserCurrentSubscription());
                });
                setShowCancelDialog(false);
              }}
              color="error"
              variant="contained"
            >
              Confirm Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Spin>
    </BillingWrapper>
  )
};

export default Billing;
